import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { subYears } from 'date-fns/subYears';
import { SmallSecondaryButton } from '../../components/buttons';
import { Tooltip } from '../../components/popover/tooltip';
import type { SortColumnOption } from '../../components/sort-popover';
import { StatusCell, type StatusVariant } from '../../components/status/status-cell';
import { defineColumn, type StrictColumnDef } from '../../components/tanstack-table/column';
import type { Vendor } from '../../data/vendor';
import { compare } from '../../helpers/column-sort';
import { formatPercent, shortCurrencyFormatter } from '../../helpers/format-currency';
import { formatDate, formatDateISO } from '../../helpers/format-date';
import type { VendorRow } from './vendor-rows';

export const VENDOR_COLUMNS_KEY = 'vendor-columns';
export const NAME_COLUMN_ID = 'Name';
export const ENABLED_COLUMN_ID = 'Enabled';
export const COGS_COLUMN_ID = '365d COGS';
export const LAST_RECEIVED_COLUMN_ID = "Last Rec'd";
export const VENDOR_PINNED_COLUMNS = [NAME_COLUMN_ID];

/**
 * What the Sort popover offers. Declared beside the column definitions so a
 * renamed column id can't silently leave the popover pointing at a column that
 * no longer exists — a mismatch TanStack answers by simply not sorting.
 */
export const VENDOR_SORT_OPTIONS: SortColumnOption[] = [
  { id: NAME_COLUMN_ID, label: 'Name', defaultDesc: false },
  { id: COGS_COLUMN_ID, label: '365d COGS', defaultDesc: true },
  { id: LAST_RECEIVED_COLUMN_ID, label: 'Last Received', defaultDesc: true },
];

const today = new Date();
const oneYearAgo = subYears(today, 1);

/**
 * One hue per integration so the Features column is scannable down the page
 * rather than a wall of one color. `Deals` reuses `info`, which the Products
 * page already assigns to Promo — the same underlying pricing data. `Receiving`
 * takes `capability`; index.css documents why that variant exists.
 */
const FEATURE_VARIANTS: Record<string, StatusVariant> = {
  Receiving: 'capability',
  Deals: 'info',
};

/**
 * A vendor that has not been received from in over a year reads as "—" rather
 * than a stale date — matching the rule the legacy table applied.
 */
function lastReceivedLabel(row: VendorRow): string {
  if (!row.lastReceived || row.lastReceived < oneYearAgo) {
    return '—';
  }

  return formatDate(row.lastReceived, today);
}

export function getVendorColumns({
  onEdit,
}: {
  onEdit: (vendor: Vendor) => void;
}): StrictColumnDef<VendorRow>[] {
  return [
    defineColumn<VendorRow>({
      id: NAME_COLUMN_ID,
      minWidth: 160,
      maxWidth: 320,
      accessorFn: (row) => row.vendor.name,
      sortingFn: (a, b) => compare(a.vendor.name, b.vendor.name),
      gloss: 'Vendor name as it appears in LiquorPOS.',
    }),

    // Disabled vendors are only on screen when "Hide Disabled" is off, and at
    // that point they must be unmistakable. The pill is SOLID, not soft:
    // status-muted-soft is #f3f4f6, which sits within a couple of values of the
    // #e8ece8 zebra row and effectively disappears on every other line — the
    // same failure mode index.css documents for row fills. Solid grey on white
    // text reads on both lanes. Enabled rows stay quiet so the eye only stops
    // on the exceptions.
    defineColumn<VendorRow>({
      id: ENABLED_COLUMN_ID,
      minWidth: 75,
      accessorFn: (row) => (row.vendor.disabled ? 'No' : 'Yes'),
      sortingFn: (a, b) => compare(Number(a.vendor.disabled), Number(b.vendor.disabled)),
      cell: ({ item }) =>
        item.vendor.disabled ? (
          <StatusCell variant="muted">Disabled</StatusCell>
        ) : (
          <span className="text-gray-400">Yes</span>
        ),
      gloss:
        'Whether this vendor is active. Disabled vendors are hidden unless you turn off the "Hide Disabled" filter.',
    }),

    defineColumn<VendorRow>({
      // minWidth clears the fixed 112px track + gap + the widest dollar amount
      // with almost nothing to spare — the track never shrinks, so anything
      // less squeezes the number rather than the bar. maxWidth is what actually
      // gives the column room at real window sizes.
      id: COGS_COLUMN_ID,
      minWidth: 200,
      maxWidth: 285,
      accessorFn: (row) => (row.cogs > 0 ? shortCurrencyFormatter(row.cogs) : '—'),
      toCSV: (row) => ({ '365d COGS': row.cogs.toFixed(2) }),
      sortingFn: (a, b) => compare(a.cogs, b.cogs),
      cell: ({ item, value }) => {
        if (item.cogs <= 0) {
          return <span className="block text-right text-gray-400">{value}</span>;
        }

        return (
          <Tooltip
            content={
              <span className="tabular-nums">
                {formatPercent(item.cogsShare)} of your total cost of goods over the last 365 days
              </span>
            }
          >
            <span className="flex cursor-default items-center gap-2">
              {/*
               * The bar gets its own lane rather than sitting behind the number.
               * index.css's "Table row state rails" block documents that a fill is
               * nearly invisible on an already-tinted zebra row — which is why row
               * hover and selection are drawn as inset lines, not fills. A solid
               * primary-400 bar reads identically on both zebra lanes and never
               * competes with the numeral's contrast.
               */}
              {/*
               * The track is a FIXED width, not flex-1. Letting it flex made every
               * row's track a different length — a short one next to $1,432,024, a
               * long one next to $39 — so two fills of the same percentage rendered
               * at different pixel lengths and the bars could not be compared down
               * the column, which is the entire reason they exist. The number takes
               * the slack instead and stays right-aligned.
               *
               * The fill is the vendor's share of TOTAL cost of goods, so the
               * whole column sums to one full track. The 1px floor only stops a
               * real amount from rendering as nothing at all — a long tail of
               * sub-percent vendors legitimately all look like slivers, because
               * that is what they are. Zero-COGS vendors never reach here.
               */}
              <span className="h-1.5 w-28 shrink-0 rounded-sm bg-gray-300">
                <span
                  data-testid="cogs-bar"
                  className="block h-full min-w-px rounded-sm bg-primary-400"
                  style={{ width: `${item.cogsShare * 100}%` }}
                />
              </span>
              <span className="flex-1 text-right tabular-nums">{value}</span>
            </span>
          </Tooltip>
        );
      },
      gloss:
        "What you sold from this vendor over the last 365 days, valued at cost. Costs come from each product's current cost in LiquorPOS, so a recent cost change reprices the whole year. Products are attributed to the vendor they were last purchased from. The bar shows each vendor's share of your total — hover any row for the exact percentage.",
    }),

    defineColumn<VendorRow>({
      id: LAST_RECEIVED_COLUMN_ID,
      minWidth: 70,
      accessorFn: lastReceivedLabel,
      toCSV: (row) => ({
        'Last Received': row.lastReceived ? formatDateISO(row.lastReceived) : '',
      }),
      sortingFn: (a, b) => compare(a.lastReceived, b.lastReceived),
      gloss: 'Most recent date any product was received from this vendor.',
    }),

    // Both pills must fit side by side without clipping. A column with no
    // maxWidth is pinned at exactly minWidth by useResponsiveColumnSizing —
    // it never receives slack — so "Receiving" + "Deals" have to fit inside
    // minWidth itself. In the table's mono face at text-xs that is ~140px of
    // content; the maxWidth then lets the column absorb spare width on wide
    // screens instead of leaving it all to Name.
    defineColumn<VendorRow>({
      id: 'Features',
      minWidth: 160,
      maxWidth: 210,
      accessorFn: (row) => row.features.join(', '),
      cell: ({ item }) =>
        item.features.length === 0 ? null : (
          <span className="flex items-center gap-1">
            {item.features.map((feature) => (
              <StatusCell
                key={feature}
                variant={FEATURE_VARIANTS[feature] ?? 'info'}
                tone="soft"
                className="shrink-0"
              >
                {feature}
              </StatusCell>
            ))}
          </span>
        ),
      gloss:
        'Orderbot integrations available for this vendor — invoice receiving and deals pricing.',
    }),

    defineColumn<VendorRow>({
      id: 'Terms',
      minWidth: 60,
      accessorFn: (row) => row.vendor.terms,
      gloss: 'Payment terms recorded in LiquorPOS.',
    }),

    defineColumn<VendorRow>({
      // Wide enough for the "Account #" header to sit on one line.
      id: 'Account #',
      minWidth: 100,
      accessorFn: (row) => row.vendor.account_number,
      gloss: 'Your account number with this vendor.',
    }),

    defineColumn<VendorRow>({
      id: 'Emails',
      minWidth: 60,
      accessorFn: (row) => row.emails.join(', '),
      sortingFn: (a, b) => compare(a.emails.length, b.emails.length),
      cell: ({ item }) =>
        item.emails.length === 0 ? null : (
          <Tooltip
            content={
              <span className="flex flex-col gap-0.5">
                {item.emails.map((email) => (
                  <span key={email}>{email}</span>
                ))}
              </span>
            }
          >
            <span className="flex w-fit cursor-default items-center gap-1 text-gray-600">
              <EnvelopeIcon className="size-3.5 shrink-0" />
              <span className="tabular-nums">{item.emails.length}</span>
            </span>
          </Tooltip>
        ),
      gloss: 'How many vendor representatives receive your orders. Hover to see the addresses.',
    }),

    defineColumn<VendorRow>({
      id: 'ID',
      minWidth: 55,
      accessorFn: (row) => row.vendor.vendor_id,
      gloss: 'Internal LiquorPOS vendor number.',
    }),

    // `ColumnConfig` has no `enableSorting` — `defineColumn` always sets it
    // true. The empty accessor makes sorting this column a no-op, which is
    // the intended behavior. Do not widen the shared type to disable it.
    defineColumn<VendorRow>({
      id: 'Edit',
      minWidth: 60,
      accessorFn: () => '',
      cell: ({ item }) => (
        <SmallSecondaryButton onClick={() => onEdit(item.vendor)}>Edit</SmallSecondaryButton>
      ),
    }),
  ];
}
